import React, {ReactNode} from "react";
import {DragDropContext, Draggable, Droppable, DropResult} from "react-beautiful-dnd";
import {ListItem} from "@mui/material";

export interface DragAndDropProps<T> {
	readonly items: T[];
	readonly children: (item: T, index: number) => ReactNode;
	readonly onItemsChanged?: (items: T[]) => void;
	readonly disabled?: boolean;
}

function reorder<T>(
	list: T[],
	startIndex: number,
	endIndex: number
): T[] {
	const result = Array.from(list);
	const [removed] = result.splice(startIndex, 1);
	result.splice(endIndex, 0, removed);
	return result;
}

export function DragAndDrop<T>(props: DragAndDropProps<T>) {
	const onDragEnd = ({destination, source}: DropResult) => {
		if (props.disabled) return;
		
		// dropped outside the list
		if (!destination) return;
		
		const newItems = reorder(props.items, source.index, destination.index);
		
		if (props.onItemsChanged)
			props.onItemsChanged(newItems);
	};
	
	return <DragDropContext onDragEnd={onDragEnd}>
		<Droppable droppableId="droppable-list" isDropDisabled={props.disabled}>
			{provided => (
				<div ref={provided.innerRef} {...provided.droppableProps}>
					{props.items.map((item, index) => {
						const children = props.children(item, index);
						if (children === undefined)
							return undefined;
						return <Draggable
							key={index.toString()}
							draggableId={index.toString()}
							index={index}
							isDragDisabled={props.disabled}
						>
							{(provided, snapshot) => (
								<ListItem
									ref={provided.innerRef}
									{...provided.draggableProps}
									{...provided.dragHandleProps}
									sx={{
										padding: "0",
										opacity: snapshot.isDragging ? "0.5" : undefined,
									}}
								>
									{children}
								</ListItem>
							)}
						</Draggable>;
					}).filter(it => !!it)}
					{provided.placeholder}
				</div>
			)}
		</Droppable>
	</DragDropContext>;
}
